import { onMount } from "@/modules/_";
import { resetWebflow } from "@/webflow/reset-webflow";
import { formGlobals } from "./inputs/_/form-globals";
import BaseInput from "./inputs/_/base-input";

// Global debug flag - can be toggled in browser console: window.FORM_DEBUG = true
declare global {
  interface Window {
    FORM_DEBUG?: boolean;
  }
}

// Import all input types
import TextInput from "./inputs/text-textarea";
import EmailInput from "./inputs/email";
import NumberInput from "./inputs/number";
import PhoneInput from "./inputs/phone";
import DateInput from "./inputs/date";
import SingleCheckboxInput from "./inputs/checkbox-single";
import CheckboxGroupInput from "./inputs/checkbox-group";
import RadioGroupInput from "./inputs/radio-group";
import ComboboxInput from "./inputs/combobox";

/**
 * Form Manager Class
 * Purpose: Manage a complete form with all input fields, validation, and submission.
 * 
 * Features:
 * - Auto-detects and initializes all input fields
 * - Manages form-wide validation
 * - Handles form submission
 * - Emits form-level events
 * 
 * Events:
 * - 'submit' - Form is being submitted (can be prevented)
 * - 'submitvalid' - Form submitted and all fields are valid
 * - 'submitinvalid' - Form submitted but has invalid fields
 * - 'validchange' - Form validity changed
 */
class FormManager extends EventTarget {
  private form: HTMLFormElement;
  private formIndex: number;
  private inputs: BaseInput[] = [];
  private submitButton: HTMLButtonElement | HTMLInputElement | null = null;
  private isValid: boolean = true;
  private _ac: AbortController;
  private static formCounter = 0;
  private hasUserInteraction: boolean = false;

  constructor(form: HTMLFormElement) {
    super();
    this.form = form;
    this.formIndex = FormManager.formCounter++;
    this._ac = new AbortController();
    
    this._init();
  }

  /**
   * Initialize the form: detect inputs, set up submit handler
   */
  private _init() {
    // Find submit button
    this.submitButton = this.form.querySelector('button[type="submit"], input[type="submit"]');

    // Handle Language hidden field
    this._initLanguageField();

    // Detect and initialize all input fields
    this._detectAndInitInputs();

    // Set up form submission handler
    this.form.addEventListener('submit', this._onSubmit.bind(this), { signal: this._ac?.signal });

    // Listen to input validchange and valuechange events to update form validity
    this.inputs.forEach(input => {
      input.addEventListener('validchange', this._onInputValidChange.bind(this), { signal: this._ac?.signal });
      input.addEventListener('valuechange', this._onInputValueChange.bind(this), { signal: this._ac?.signal });
    });

    // Initial validity check
    this._updateFormValidity();
  }

  /**
   * Initialize Language hidden field with document lang attribute
   */
  private _initLanguageField() {
    const languageField = this.form.querySelector('input[type="hidden"][name="Language"]') as HTMLInputElement;
    if (languageField) {
      const docLang = document.documentElement.lang || 'en';
      languageField.value = docLang;
    }
  }

  /**
   * Detect all input fields and initialize appropriate input classes
   */
  private _detectAndInitInputs() {
    const wraps = Array.from(this.form.querySelectorAll(formGlobals.fieldWrapSelector));
    
    wraps.forEach((wrap, index) => {
      const input = this._createInputForWrap(wrap as HTMLElement, index);
      if (input) {
        this.inputs.push(input);
      }
    });
  }

  /**
   * Create the appropriate input instance based on the wrap element's content
   */
  private _createInputForWrap(wrap: HTMLElement, wrapIndex: number): BaseInput | null {
    // Check for combobox
    if (wrap.querySelector('[role="combobox"]')) {
      return new ComboboxInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for radio group
    if (wrap.querySelector('[role="radiogroup"]')) {
      return new RadioGroupInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for checkbox group
    if (wrap.querySelector('[role="group"]') && wrap.querySelectorAll('input[type="checkbox"]').length > 1) {
      return new CheckboxGroupInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for single checkbox
    if (wrap.querySelector('input[type="checkbox"]')) {
      return new SingleCheckboxInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for date input (both native date input and text input with data-datepicker)
    if (wrap.querySelector('input[type="date"]') || wrap.querySelector('input[type="text"][data-datepicker="true"]')) {
      return new DateInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for email input
    if (wrap.querySelector('input[type="email"]')) {
      return new EmailInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for number input
    if (wrap.querySelector('input[type="number"]')) {
      return new NumberInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for phone/tel input
    if (wrap.querySelector('input[type="tel"]')) {
      return new PhoneInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    // Check for text/textarea (fallback)
    if (wrap.querySelector('input[type="text"], textarea')) {
      return new TextInput(wrap, this.formIndex as any, wrapIndex as any);
    }

    return null;
  }

  /**
   * Handle form submission
   */
  private _onSubmit(e: SubmitEvent) {
    // Validate all fields
    this.inputs.forEach(input => {
      input.validateNow();
    });

    this._updateFormValidity();

    // Emit submit event (can be prevented)
    const submitEvent = new CustomEvent('submit', {
      detail: {
        form: this.form,
        valid: this.isValid,
        formData: this.getFormData(),
        instance: this
      },
      bubbles: true,
      cancelable: true
    });
    
    const defaultPrevented = !this.dispatchEvent(submitEvent);

    if (defaultPrevented) {
      e.preventDefault();
      return;
    }

    // If form is invalid, prevent default submission
    if (!this.isValid) {
      e.preventDefault();
      this._handleInvalidSubmit();
      return;
    }

    // Form is valid - emit submitvalid event
    this.dispatchEvent(new CustomEvent('submitvalid', {
      detail: {
        form: this.form,
        formData: this.getFormData(),
        instance: this
      },
      bubbles: true
    }));
  }

  /**
   * Handle invalid form submission
   */
  private _handleInvalidSubmit() {
    // Focus first invalid field
    const firstInvalid = this.inputs.find(input => !input.isValid());
    if (firstInvalid && firstInvalid.wrap) {
      const focusable = firstInvalid.wrap.querySelector('input, textarea, button, [role="combobox"]') as HTMLElement;
      if (focusable) {
        focusable.focus();
      }
    }

    // Emit submitinvalid event
    this.dispatchEvent(new CustomEvent('submitinvalid', {
      detail: {
        form: this.form,
        invalidFields: this.inputs.filter(input => !input.isValid()).map(input => ({
          id: input.inputId,
          state: input.getState()
        })),
        instance: this
      },
      bubbles: true
    }));
  }

  /**
   * Handle input validity changes
   */
  private _onInputValidChange(e: Event) {
    // Mark that user has interacted with the form
    const detail = (e as CustomEvent).detail;
    if (detail?.instance?.blurred) {
      this.hasUserInteraction = true;
    }
    
    // Debug logging
    if (window.FORM_DEBUG) {
      console.log('[Form] validchange event:', {
        inputId: detail?.instance?.inputId,
        valid: detail?.valid,
        blurred: detail?.instance?.blurred
      });
    }
    
    this._updateFormValidity();
  }

  /**
   * Handle input value changes
   * This ensures form validity is updated even if fields haven't been blurred yet
   */
  private _onInputValueChange(e: Event) {
    const detail = (e as CustomEvent).detail;
    const inputInstance = detail?.instance;
    
    // Check if any input has a value (user is actively filling the form)
    const hasAnyValue = this.inputs.some(input => {
      const state = input.getState() as any;
      return (state.value && state.value !== '') || state.checked === true;
    });
    
    if (hasAnyValue) {
      this.hasUserInteraction = true;
    }
    
    // If the input that changed has a value and is required, validate it
    // This handles the case where user fills a field but hasn't blurred yet
    if (inputInstance) {
      const state = inputInstance.getState() as any;
      const hasValue = (state.value && state.value.toString().trim() !== '') || state.checked === true;
      
      // Debug logging
      if (window.FORM_DEBUG) {
        console.log('[Form] valuechange event:', {
          inputId: state.id,
          value: state.value || '(empty)',
          required: state.required,
          hasValue: hasValue,
          willValidate: state.required && hasValue
        });
      }
      
      // If field has value and is required, trigger validation to update its valid state
      if (state.required && hasValue) {
        inputInstance.validateNow();
      }
    }
    
    // Update form validity based on current input states
    this._updateFormValidity();
  }

  /**
   * Update form-level validity state
   */
  private _updateFormValidity() {
    const wasValid = this.isValid;
    this.isValid = this.inputs.every(input => input.isValid());

    // Debug logging
    if (window.FORM_DEBUG) {
      const invalidCount = this.inputs.filter(input => !input.isValid()).length;
      console.log('[Form] _updateFormValidity:', {
        wasValid: wasValid,
        isValid: this.isValid,
        changed: wasValid !== this.isValid,
        invalidCount: invalidCount,
        totalInputs: this.inputs.length
      });
      
      // Show invalid fields details if form is invalid and user has interacted
      if (!this.isValid && this.hasUserInteraction) {
        const invalidFields = this.inputs.filter(input => !input.isValid());
        console.group(`[Form ${this.formIndex}] Invalid Fields preventing submission (${invalidFields.length})`);
        invalidFields.forEach(input => {
          const state = input.getState() as any;
          console.log(`- ${state.id}:`, {
            required: state.required,
            value: state.value || state.checked || '(no value)',
            blurred: state.blurred,
            valid: input.isValid()
          });
        });
        console.groupEnd();
      }
    }

    // Update submit button state
    if (this.submitButton) {
      this.submitButton.disabled = !this.isValid;
    }

    // Emit validchange if it changed
    if (wasValid !== this.isValid) {
      this.dispatchEvent(new CustomEvent('validchange', {
        detail: {
          valid: this.isValid,
          form: this.form,
          instance: this
        },
        bubbles: true
      }));
    }
  }

  /**
   * Get form data as object
   */
  getFormData(): Record<string, any> {
    const data: Record<string, any> = {};

    this.inputs.forEach(input => {
      const state = input.getState() as any;
      
      // Handle different input types
      if ('values' in state && Array.isArray(state.values)) {
        // Checkbox group
        data[input.inputId] = state.values;
      } else if ('checked' in state) {
        // Single checkbox
        data[input.inputId] = state.checked ? (state.value || true) : null;
      } else if ('value' in state) {
        // Standard inputs
        data[input.inputId] = state.value;
      }
    });

    // Also include native form data
    const formData = new FormData(this.form);
    formData.forEach((value, key) => {
      if (!(key in data)) {
        data[key] = value;
      }
    });

    return data;
  }

  /**
   * Validate all fields
   */
  validateAll(): boolean {
    this.inputs.forEach(input => {
      input.validateNow();
    });
    this._updateFormValidity();
    return this.isValid;
  }

  /**
   * Get all input instances
   */
  getInputs(): BaseInput[] {
    return [...this.inputs];
  }

  /**
   * Get input by ID
   */
  getInputById(id: string): BaseInput | undefined {
    return this.inputs.find(input => input.inputId === id);
  }

  /**
   * Get all invalid inputs
   */
  getInvalidInputs(): BaseInput[] {
    return this.inputs.filter(input => !input.isValid());
  }

  /**
   * Debug: Log all fields and their validity state
   */
  debugValidation() {
    console.group(`%c[Form ${this.formIndex}] Validation Debug`, 'font-weight: bold; font-size: 14px');
    
    // Show how to enable live debugging
    if (!window.FORM_DEBUG) {
      console.log('%c💡 Tip: Enable live debug logging with: %cwindow.FORM_DEBUG = true', 
        'color: #888', 'color: #0066cc; font-family: monospace');
    }
    
    console.log('%cForm Status:', 'font-weight: bold', {
      isValid: this.isValid,
      submitButtonDisabled: this.submitButton?.disabled,
      totalInputs: this.inputs.length,
      hasUserInteraction: this.hasUserInteraction,
      liveDebug: window.FORM_DEBUG ? 'enabled' : 'disabled'
    });
    
    const invalidFields = this.getInvalidInputs();
    const validFields = this.inputs.filter(input => input.isValid());

    // Show invalid fields first (these are blocking submission)
    if (invalidFields.length > 0) {
      console.group(`%c❌ Invalid Fields (${invalidFields.length}) - BLOCKING SUBMISSION`, 'color: red; font-weight: bold');
      invalidFields.forEach((input, idx) => {
        const state = input.getState() as any;
        const wrap = input.wrap;
        console.log(`${idx + 1}. ${state.id}:`, {
          '⚠️ Problem': !state.blurred ? 'User has not interacted yet' : 'Invalid value',
          required: state.required,
          value: state.value !== undefined ? state.value : (state.checked !== undefined ? state.checked : '(no value)'),
          blurred: state.blurred,
          element: wrap
        });
      });
      console.groupEnd();
    }

    // Show valid fields
    if (validFields.length > 0) {
      console.group(`%c✅ Valid Fields (${validFields.length})`, 'color: green; font-weight: bold');
      validFields.forEach((input, idx) => {
        const state = input.getState() as any;
        console.log(`${idx + 1}. ${state.id}:`, {
          required: state.required,
          value: state.value !== undefined ? state.value : (state.checked !== undefined ? state.checked : '(no value)')
        });
      });
      console.groupEnd();
    }

    // Helpful tips
    if (invalidFields.length > 0) {
      console.log('%c💡 Tip:', 'font-weight: bold', 
        'Required fields start as invalid and become valid after user fills them correctly. ' +
        'If fields are not required but still invalid, check aria-required attributes.'
      );
    }
    
    console.groupEnd();

    // Return summary for programmatic access
    return {
      isValid: this.isValid,
      totalInputs: this.inputs.length,
      validCount: validFields.length,
      invalidCount: invalidFields.length,
      invalidFields: invalidFields.map(input => {
        const state = input.getState() as any;
        return {
          id: state.id,
          required: state.required,
          blurred: state.blurred,
          value: state.value !== undefined ? state.value : state.checked
        };
      })
    };
  }

  /**
   * Destroy form manager and clean up
   */
  destroy() {
    this._ac.abort();
    this.inputs.forEach(input => {
      input.destroy();
    });
    this.inputs = [];
    this.form = null as any;
    this.submitButton = null;
  }
}

// Global helper function for debugging all forms on the page
if (typeof window !== 'undefined') {
  (window as any).debugAllForms = function() {
    const forms = document.querySelectorAll('form[data-module="form"]');
    if (forms.length === 0) {
      console.log('No forms with data-module="form" found on page');
      return;
    }
    
    console.group(`%c🔍 Debugging ${forms.length} form(s)`, 'font-size: 16px; font-weight: bold');
    
    // Show current debug state
    if (window.FORM_DEBUG) {
      console.log('%c✅ Live debug logging is ENABLED', 'color: green; font-weight: bold');
    } else {
      console.log('%c💡 Enable live debug logging with: %cwindow.FORM_DEBUG = true', 
        'color: #888', 'color: #0066cc; font-family: monospace');
    }
    console.log(''); // Empty line for readability
    
    forms.forEach((form, idx) => {
      const formManager = (form as any).__formManager;
      if (formManager) {
        console.log(`\n--- Form ${idx + 1} ---`);
        formManager.debugValidation();
      } else {
        console.warn(`Form ${idx + 1} has no FormManager instance`);
      }
    });
    console.groupEnd();
  };
}

// Export form manager instance creator
export default function (element: HTMLElement, dataset: DOMStringMap) {
  const form = element as HTMLFormElement;
  
  if (!form || form.tagName !== 'FORM') {
    console.warn('Form module: element is not a form');
    return;
  }

  let formManager: FormManager | null = null;
  

  onMount(() => {
    setTimeout(() => {
      resetWebflow();
      formManager = new FormManager(form);
      
      // Store instance on form element for debugging access
      (form as any).__formManager = formManager;
    }, 500);

  });

  // Return cleanup function
  return () => {
    if (formManager) {
      formManager.destroy();
      delete (form as any).__formManager;
    }
  };
}
